"use client";

import { useEffect, useRef, useState, useCallback } from "react";

interface PdfThumbnailGridProps {
  file: File | null;
  mode: "select" | "reorder" | "rotate";
  selectedPages?: Set<number>;
  onSelectionChange?: (pages: Set<number>) => void;
  pageOrder?: number[];
  onOrderChange?: (order: number[]) => void;
  rotations?: Record<number, number>;
  onRotationsChange?: (rotations: Record<number, number>) => void;
}

export default function PdfThumbnailGrid({
  file,
  mode,
  selectedPages,
  onSelectionChange,
  pageOrder,
  onOrderChange,
  rotations,
  onRotationsChange,
}: PdfThumbnailGridProps) {
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  useEffect(() => {
    if (!file) {
      setThumbnails([]);
      setPageCount(0);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    const renderThumbs = async () => {
      try {
        const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf.mjs");
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
          import.meta.url
        ).toString();

        const data = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data }).promise;
        const count = pdf.numPages;
        if (cancelled) return;
        setPageCount(count);

        const thumbs: string[] = [];
        for (let i = 0; i < count; i++) {
          const page = await pdf.getPage(i + 1);
          const viewport = page.getViewport({ scale: 0.3 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext("2d")!;
          await page.render({ canvasContext: ctx, viewport, canvas }).promise;
          thumbs.push(canvas.toDataURL("image/jpeg", 0.7));
          if (cancelled) return;
        }
        setThumbnails(thumbs);
      } catch (err) {
        console.error("PdfThumbnailGrid render error:", err);
        if (!cancelled) setError("Failed to load PDF pages");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    renderThumbs();
    return () => { cancelled = true; };
  }, [file]);

  const togglePage = useCallback(
    (idx: number) => {
      if (mode !== "select" || !onSelectionChange) return;
      const next = new Set(selectedPages);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      onSelectionChange(next);
    },
    [mode, selectedPages, onSelectionChange]
  );

  const rotatePage = useCallback(
    (idx: number) => {
      if (mode !== "rotate" || !onRotationsChange) return;
      const current = rotations?.[idx] || 0;
      onRotationsChange({ ...rotations, [idx]: (current + 90) % 360 });
    },
    [mode, rotations, onRotationsChange]
  );

  const handleDragStart = (idx: number) => {
    if (mode !== "reorder") return;
    setDragIdx(idx);
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    if (mode !== "reorder" || dragIdx === null) return;
    e.preventDefault();
  };

  const handleDrop = (idx: number) => {
    if (mode !== "reorder" || dragIdx === null || !onOrderChange) return;
    const order = pageOrder || Array.from({ length: pageCount }, (_, i) => i);
    const newOrder = [...order];
    const [removed] = newOrder.splice(dragIdx, 1);
    newOrder.splice(idx, 0, removed);
    onOrderChange(newOrder);
    setDragIdx(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <div className="relative h-8 w-8">
            <div className="absolute inset-0 rounded-full border-2 border-slate-200" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-brand-600 animate-spin" />
          </div>
          <p className="text-sm text-slate-500">Loading pages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-red-600 py-4 text-center">{error}</p>;
  }

  if (thumbnails.length === 0) return null;

  const displayOrder = pageOrder || Array.from({ length: pageCount }, (_, i) => i);

  return (
    <div>
      {mode === "select" && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">
            {selectedPages?.size || 0} of {pageCount} pages selected
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => onSelectionChange?.(new Set(Array.from({ length: pageCount }, (_, i) => i)))}
              className="text-xs text-brand-600 hover:text-brand-700 font-medium"
            >
              Select all
            </button>
            <button
              onClick={() => onSelectionChange?.(new Set())}
              className="text-xs text-slate-400 hover:text-slate-600 font-medium"
            >
              Deselect all
            </button>
          </div>
        </div>
      )}

      {mode === "rotate" && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">Click a page to rotate it 90°</p>
          <button
            onClick={() => {
              const all: Record<number, number> = {};
              for (let i = 0; i < pageCount; i++) {
                all[i] = ((rotations?.[i] || 0) + 90) % 360;
              }
              onRotationsChange?.(all);
            }}
            className="text-xs text-brand-600 hover:text-brand-700 font-medium"
          >
            Rotate all
          </button>
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
        {displayOrder.map((pageIdx, displayIdx) => {
          const isSelected = mode === "select" && selectedPages?.has(pageIdx);
          const rotation = rotations?.[pageIdx] || 0;

          return (
            <div
              key={`${pageIdx}-${displayIdx}`}
              draggable={mode === "reorder"}
              onDragStart={() => handleDragStart(displayIdx)}
              onDragOver={(e) => handleDragOver(e, displayIdx)}
              onDrop={() => handleDrop(displayIdx)}
              onClick={() => {
                if (mode === "select") togglePage(pageIdx);
                if (mode === "rotate") rotatePage(pageIdx);
              }}
              className={`relative cursor-pointer rounded-lg border-2 overflow-hidden transition-all duration-150 ${
                isSelected
                  ? "border-[var(--color-gold)] ring-2 ring-[var(--color-gold-muted)] shadow-md"
                  : dragIdx === displayIdx
                  ? "border-brand-400 opacity-50"
                  : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
              }`}
            >
              <div className="aspect-[3/4] bg-slate-50 flex items-center justify-center overflow-hidden">
                {thumbnails[pageIdx] && (
                  <img
                    src={thumbnails[pageIdx]}
                    alt={`Page ${pageIdx + 1}`}
                    className="w-full h-full object-contain"
                    style={{ transform: `rotate(${rotation}deg)` }}
                  />
                )}
              </div>
              <div className="px-2 py-1.5 bg-white border-t border-slate-100 text-center">
                <span className="text-xs text-slate-500 font-medium">{pageIdx + 1}</span>
                {rotation > 0 && (
                  <span className="ml-1 text-[10px] text-brand-600">{rotation}°</span>
                )}
              </div>

              {/* Selection checkmark */}
              {mode === "select" && isSelected && (
                <div className="absolute top-1.5 right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--color-gold)]">
                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
              )}

              {/* Reorder handle */}
              {mode === "reorder" && (
                <div className="absolute top-1.5 left-1.5 flex h-5 w-5 items-center justify-center rounded bg-white/80 shadow-sm">
                  <svg className="h-3 w-3 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
